import { assign, fromPromise, setup } from "xstate";
import type { DoneActorEvent } from "xstate";

import {
  MemberConsultationOutcome,
  getMemberConsultations,
  startMemberConsultation,
  getMemberConsultationOutcome,
  GetMemberConsultationsItem,
  getMemberships,
  Member,
  MembershipType,
  submitRating,
  UserRating,
  ServiceRating,
  MemberConsultationType,
  ProductAdministratorSupportedServiceChannelAvailability,
  getMemberProductPaymentRates,
  MemberProductPaymentRatesItem,
  MemberConsultationStatus,
  getDefaultPayGMedicalAssistanceAvailabilityData,
  getMemberServiceOfferingUsage,
  MemberServiceOfferingType,
  MemberServiceOfferingUsage,
  MemberServiceOfferingStatus,
  MemberProfile,
  getMemberProfile,
  getDirectToDoctorPrimarySymptoms,
  Status,
  DirectToDoctorPrimarySymptom,
  getMembershipDependents,
  MembershipDependent,
  updateMembershipDependents,
  MemberProductPaymentRatesChargeCode,
  GetMemberProductPaymentRatesStatus,
  getContractsToSign,
  getConsultationPaymentReceipt,
  ConsultationPaymentReceipt,
  applyDiscountVoucher,
  ApplyVoucherResponse,
  ApplyVoucherErrors,
  MemberVoucherOfferingUsage,
  findUNU01Membership,
  initiateConsultationPayment,
  InitiateConsultationPaymentParams,
  InitiateConsultationPaymentResponse,
  memberRequestPayment,
} from "../../../../services/core-api-adapter";
import {
  getCurrentFormattedDay,
  getCurrentFormattedTime,
  isPublicHolidayInSouthAfrica,
  debugLogger,
} from "../../../../utils";

const log = debugLogger.forMachine("IntercareMedicalAssistance");
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";
import { ConsultationRoute, ConsultationMatch } from "../../types";

const MAX_POLLING_TIME_IN_SECONDS =
  Number(
    import.meta.env.VITE_APP_INTERCARE_MEDICAL_ASSISTANCE_MAX_POLLING_TIME
  ) || 100;
const POLLING_INTERVAL_IN_MILLISECONDS =
  Number(
    import.meta.env.VITE_APP_INTERCARE_MEDICAL_ASSISTANCE_POLLING_INTERVAL
  ) || 2000;

interface Context {
  activeConsultationListResponse: Array<GetMemberConsultationsItem>;
  memberProfileResponse: MemberProfile | null;
  membersList: Array<Member>;
  currentUserMemberId: string | null;
  memberDependantsList: Array<MembershipDependent>;
  selectedDependent: MembershipDependent | null;
  selectedConsultation: GetMemberConsultationsItem | null;
  consultationOutcomeResponse: GetMemberConsultationsItem | null;
  consultationRatesResponse: MemberProductPaymentRatesItem[] | null;
  consultationType: MemberConsultationType;
  userRating: UserRating;
  consultationAvailabilityData: ProductAdministratorSupportedServiceChannelAvailability[];
  startConsultationTime: Date | null;
  queryParamConsultationId: string;
  queryParamDocMedsBundles: boolean;
  primarySymptomIds: string[];
  isDirectToDoctorSelected: boolean;
  isDoctorMedicineBundleSelected: boolean;
  isNurseConsultationSelected: boolean;
  memberServiceOfferingUsages: MemberServiceOfferingUsage[] | null;
  primarySymptomsResponse: DirectToDoctorPrimarySymptom[] | null;
  selectedVoucherOrPromo: MemberServiceOfferingUsage | null;
  contractsToSign: null;
  oneVoucherPin?: string | undefined;
  oneVoucherRedemptionError: any;
  paymentReceiptResponse?: ConsultationPaymentReceipt | null;
  isFromOrderSummary?: boolean;
  isFromMyPurchases?: boolean;
  selectedDiscountVoucher?: MemberServiceOfferingUsage | null;
  capturedDiscountVoucherCode?: string | null;
  applyDiscountVoucherResponse?: ApplyVoucherResponse | null;
  applyDiscountVoucherResponseError: any;
  isUseVoucherLater: boolean;
  matchedConsultation?: ConsultationMatch | null;
  skipPayment?: boolean;
  isResumingPaymentFlow?: boolean;
  detectionError?: Error | null;
  paymentMethod?: "card" | "voucher";
  consultationPaymentMemberLedgerId: string | null;
}

const defaultAvailabilityData =
  getDefaultPayGMedicalAssistanceAvailabilityData();

/**
 * Maps consultation type selection to charge code for API matching
 * MUST be invoked only when VITE_APP_FEATURE_ENABLE_SINGLE_CONSULTATION_ID toggle is enabled
 *
 * @param context - State machine context with consultation type flags
 * @returns Charge code string or null if no type selected
 */
export function getChargeCodeForConsultationType(
  context: Context
): string | null {
  if (context.isDirectToDoctorSelected) {
    return "DIGITAL_DOCTOR_CONSULT";
  }
  if (context.isNurseConsultationSelected) {
    return "ONLINE_DOCTOR_CONSULTATION";
  }
  if (context.isDoctorMedicineBundleSelected) {
    return "DIGITAL_DOCTOR_MEDICINE_CONSULT";
  }
  // Fallback to selected consultation's charge code (for continuing active consultations)
  if (context.selectedConsultation?.chargeCode) {
    return context.selectedConsultation.chargeCode;
  }
  return null;
}

/**
 * Finds the consultation rate matching the current consultation type.
 * Uses memberProfileResponse.consultation.consultationRates as primary source,
 * falls back to consultationRatesResponse if member profile rates unavailable.
 *
 * @param context - State machine context with consultation rates
 * @returns Matched rate item or null if not found
 */
export function findConsultationRate(
  context: Context
): MemberProductPaymentRatesItem | null {
  const chargeCode = getChargeCodeForConsultationType(context);
  if (!chargeCode) {
    return null;
  }

  const rates =
    context.memberProfileResponse?.consultation?.consultationRates ||
    context.consultationRatesResponse;

  return rates?.find((rate) => rate.chargeCode === chargeCode) || null;
}

/**
 * Gets the product amount (order total) for payment initiation
 * Calculates the discounted total when a voucher is applied, matching the UI display
 *
 * @param context - State machine context with consultation rates and optional discount voucher
 * @returns Payment amount in rands as decimal (e.g., 204 for R204), or 0 if not found or invalid
 */
export function getProductAmount(context: Context): number {
  // Get the consultation rate (original amount in rands)
  const matchedRate = findConsultationRate(context);

  if (!matchedRate) {
    log.event(
      `No payment rate found for chargeCode: ${getChargeCodeForConsultationType(context)}`,
      { chargeCode: getChargeCodeForConsultationType(context) }
    );
    return 0;
  }

  const originalAmountInRands = parseFloat(matchedRate.amount);
  if (isNaN(originalAmountInRands) || originalAmountInRands < 0) {
    log.event(`Invalid consultation cost: ${matchedRate.amount}`, {
      amount: matchedRate.amount,
    });
    return 0;
  }

  // If discount voucher is applied AND not using voucher later, calculate discounted total
  const discountVoucherDetails =
    context.selectedDiscountVoucher?.discountVoucherDetails;

  if (discountVoucherDetails && !context.isUseVoucherLater) {
    // discountAmount is in cents, convert to rands
    const discountAmountInRands =
      (discountVoucherDetails.discountAmount || 0) / 100;

    // Calculate order total: original - discount (same as UI display)
    const orderTotalInRands = originalAmountInRands - discountAmountInRands;

    // Return as decimal, ensure non-negative
    return Math.max(0, orderTotalInRands);
  }

  // No discount applied - return original amount in rands
  return originalAmountInRands;
}

/**
 * Gets the service type based on consultation type
 * Maps chargeCode to service type for the initiate-consultation-payment API
 *
 * @param context - State machine context
 * @returns Service type string (e.g., "DIRECT_TO_GP")
 */
export function getServiceType(context: Context): string {
  const chargeCode = getChargeCodeForConsultationType(context);
  const serviceTypeMap: Record<string, string> = {
    DIGITAL_DOCTOR_CONSULT: "DIRECT_TO_GP",
    ONLINE_DOCTOR_CONSULTATION: "BLENDED",
    DIGITAL_DOCTOR_MEDICINE_CONSULT: "DIRECT_TO_GP",
  };
  return chargeCode
    ? serviceTypeMap[chargeCode] || "DIRECT_TO_GP"
    : "DIRECT_TO_GP";
}

/**
 * Gets the offering ID for payment initiation based on consultation type.
 *
 * @param context - State machine context with consultation rates
 * @returns Offering ID string, or empty string if no matching rate found
 */
export function getOfferingId(context: Context): string {
  return findConsultationRate(context)?.offeringId || "";
}

/**
 * Checks whether an applied discount voucher belongs to the consultation
 * currently being booked. Mirrors the Care Card filter rule: an ALL_SERVICES
 * voucher is always valid, otherwise the voucher's offeringId must match the
 * current consultation's offeringId. An unresolvable consultation offeringId
 * is treated as a mismatch (fail-safe).
 *
 * A missing voucher payload is likewise treated as not applicable.
 *
 * @param voucher - The voucher usage returned by applyDiscountVoucher
 * @param context - State machine context with the selected consultation
 */
export function isDiscountVoucherApplicableToConsultation(
  voucher: MemberVoucherOfferingUsage | null | undefined,
  context: Context
): boolean {
  if (!voucher) {
    return false;
  }
  if (voucher.serviceOfferingType === MemberServiceOfferingType.ALL_SERVICES) {
    return true;
  }
  const offeringId = getOfferingId(context);
  return Boolean(offeringId) && voucher.offeringId === offeringId;
}

/**
 * Extracts service offering usage ID for payment initiation.
 * Checks Care Card vouchers first, then discount vouchers.
 *
 * Payment method detection:
 * - Care Card: selectedVoucherOrPromo.id is set
 * - Discount voucher: selectedDiscountVoucher.id is set and not using voucher later
 * - 1Voucher: oneVoucherPin is set, no usage ID needed
 * - Ecentric (no voucher): returns null
 *
 * @param context - State machine context with payment method state
 * @returns Usage ID string or null when no voucher applies
 */
export function getMemberServiceOfferingUsageId(
  context: Context
): string | null {
  if (context.selectedVoucherOrPromo?.id) {
    return context.selectedVoucherOrPromo.id;
  }

  if (context.selectedDiscountVoucher?.id && !context.isUseVoucherLater) {
    return context.selectedDiscountVoucher.id;
  }

  return null;
}

/**
 * Extracts 1Voucher PIN for payment initiation.
 * Returns null for Ecentric card payment and Care Card flows.
 *
 * Payment method detection:
 * - 1Voucher: oneVoucherPin is set, selectedVoucherOrPromo is null
 * - Care Card: selectedVoucherOrPromo.id is set, oneVoucherPin is null
 * - Ecentric: Both are null (card/InstantEFT payment)
 *
 * @param context - State machine context with payment method state
 * @returns 1Voucher PIN string or null for non-1Voucher payment methods
 */
export function getOneVoucherPin(context: Context): string | null {
  return context.oneVoucherPin || null;
}

/**
 * Type guard that checks if the consultation outcome indicates a pending payment
 *
 * @param outcome - The consultation outcome to check
 * @returns True if outcome is PAYMENT_REQUESTED, PAYMENT_FAILED, or START
 */
export function isPaymentPendingOutcome(
  outcome: MemberConsultationOutcome
): outcome is
  | MemberConsultationOutcome.PAYMENT_REQUESTED
  | MemberConsultationOutcome.PAYMENT_FAILED
  | MemberConsultationOutcome.START {
  return (
    outcome === MemberConsultationOutcome.PAYMENT_REQUESTED ||
    outcome === MemberConsultationOutcome.PAYMENT_FAILED ||
    outcome === MemberConsultationOutcome.START
  );
}

/**
 * Gets the consultation ID from context, preferring selected consultation
 *
 * @param context - State machine context
 * @returns Consultation ID string, or empty string if not found
 */
export function getConsultationId(context: Context): string {
  return (
    context.selectedConsultation?.consultationId ||
    context.queryParamConsultationId ||
    ""
  );
}

/**
 * Gets the patient ID from context, with fallback to main member
 *
 * @param context - State machine context
 * @returns Patient ID string, or empty string if not found
 */
export function getPatientId(context: Context): string {
  if (context.selectedDependent?.memberId) {
    return context.selectedDependent.memberId;
  }

  if (context.selectedConsultation?.memberId) {
    return context.selectedConsultation.memberId;
  }

  const mainMember = context.membersList.find(
    (member) => member.membershipDetails.type === MembershipType.MAIN_MEMBER
  );

  return mainMember?.memberDetails?.id || "";
}

/**
 * Gets the main member's ID (the logged-in payer), regardless of selected dependent
 *
 * @param context - State machine context
 * @returns Main member ID string, or empty string if not found
 */
export function getMainMemberId(context: Context): string {
  const mainMember = context.membersList.find(
    (member) => member.membershipDetails.type === MembershipType.MAIN_MEMBER
  );

  return mainMember?.memberDetails?.id || "";
}

/**
 * Validates if a consultation has a valid HTTPS redirect URL
 * Required when routing to Intercare (PAYMENT_SUCCEEDED outcome)
 * MUST be invoked only when VITE_APP_FEATURE_ENABLE_SINGLE_CONSULTATION_ID toggle is enabled
 *
 * @param consultation - The consultation to validate
 * @returns True if redirectUrl exists and is an HTTPS URL
 */
export function hasValidRedirectUrl(
  consultation: ConsultationMatch | null
): boolean {
  if (!consultation || !consultation.redirectUrl) {
    return false;
  }

  try {
    const url = new URL(consultation.redirectUrl);
    return url.protocol === "https:";
  } catch {
    return false;
  }
}

const PAYMENT_FLOW_OUTCOMES = [
  "PAYMENT_REQUESTED",
  "PAYMENT_FAILED",
  "TRIAGE_STARTED",
  "TRIAGE_ENDED",
  "START",
];

const INTERCARE_LINK_OUTCOMES = [
  "SESSION_STARTED",
  "PAYMENT_SUCCEEDED",
  "DIGITAL_CONSULT_STARTED",
  "DIGITAL_CONSULT_ENDED",
  "REFUND_REQUESTED",
  "NO_REFUND",
];

/**
 * Determines routing decision based on consultation outcome
 * MUST be invoked only when VITE_APP_FEATURE_ENABLE_SINGLE_CONSULTATION_ID toggle is enabled
 *
 * @param existing - Matched consultation or null
 * @returns Route enum indicating which flow to follow
 */
export function getConsultationRoute(
  existing: ConsultationMatch | null
): ConsultationRoute {
  if (!existing || !existing.outcome) {
    return ConsultationRoute.NEW_CONSULTATION;
  }

  if (PAYMENT_FLOW_OUTCOMES.includes(existing.outcome)) {
    return ConsultationRoute.PAYMENT_FLOW;
  }

  if (
    INTERCARE_LINK_OUTCOMES.includes(existing.outcome) &&
    hasValidRedirectUrl(existing)
  ) {
    return ConsultationRoute.INTERCARE_LINK;
  }

  return ConsultationRoute.DEFER_TO_WORKFLOW;
}

/**
 * Maps a ConsultationMatch to GetMemberConsultationsItem format
 * Used when routing based on matched consultation outcome
 *
 * @param match - The matched consultation from idempotency check
 * @returns Formatted consultation item for state machine context
 */
function mapMatchedToSelectedConsultation(
  match: ConsultationMatch
): GetMemberConsultationsItem {
  return {
    consultationId: match.consultationId,
    memberId: match.memberId,
    outcome: match.outcome as MemberConsultationOutcome,
    chargeAmountFormatted: match.chargeAmountFormatted || "",
    chargeCode: match.chargeCode as MemberProductPaymentRatesChargeCode,
    redirectUrl: match.redirectUrl || "",
    serviceProviderName: match.serviceProviderName || "",
    startedOn: match.startedOn,
    status: match.status as MemberConsultationStatus,
    isDependant: match.isDependant ?? false,
    mainMemberId: match.mainMemberId || "",
    consultationTypeName: match.consultationTypeName || "",
  } as GetMemberConsultationsItem;
}

/**
 * Determines if screen order should swap (member selection before service type)
 * Swaps when ALL THREE conditions are met:
 * - User has dependents
 * - No query params (normal entry, not deep link)
 * - Dependent not already selected (prevents re-showing selection)
 *
 * Note: Active consultations no longer block screen swap (Dependents Per Day).
 * When user has dependents, they should always select the member first. Consultation
 * matching happens per-member AFTER member selection, allowing:
 * - Main member to start consultation for dependent even when main member has active consultation
 * - Each dependent to have their own consultation independently
 *
 * MUST be invoked only when VITE_APP_FEATURE_ENABLE_SINGLE_CONSULTATION_ID toggle is enabled
 *
 * @param context - State machine context with dependents list, query params, and selected dependent
 * @returns True if screen order should swap (show member selection first)
 */
export function shouldSwapScreenOrder(context: Context): boolean {
  const hasQueryParams =
    Boolean(context.queryParamConsultationId) ||
    (context.primarySymptomIds?.length || 0) > 0 ||
    Boolean(context.queryParamDocMedsBundles);

  const hasDependents = (context.memberDependantsList?.length || 0) > 0;
  const dependentAlreadySelected = context.selectedDependent !== null;

  return hasDependents && !hasQueryParams && !dependentAlreadySelected;
}

/**
 * Creates a reusable state entry logger action for XState
 * Reduces duplication by extracting common logging patterns
 *
 * @param stateName - The name of the state being entered
 * @param contextExtractor - Function that extracts relevant context properties to log
 * @returns An XState entry action function
 */
function createStateEntryLogger<TContext>(
  stateName: string,
  contextExtractor: (context: TContext) => Record<string, any>
) {
  return ({ context }: { context: TContext }) => {
    log.stateEntry(stateName, contextExtractor(context));
  };
}

/**
 * Creates a reusable decision state logger action for XState
 * Similar to createStateEntryLogger but uses debugLogger.decisionState
 *
 * @param stateName - The name of the decision state being entered
 * @param contextExtractor - Function that extracts relevant context properties to log
 * @returns An XState entry action function
 */
function createDecisionStateLogger<TContext>(
  stateName: string,
  contextExtractor: (context: TContext) => Record<string, any>
) {
  return ({ context }: { context: TContext }) => {
    log.decisionState(stateName, contextExtractor(context));
  };
}

const initialContextValues: Context = {
  activeConsultationListResponse: [],
  memberProfileResponse: null,
  isDoctorMedicineBundleSelected: false,
  membersList: [],
  currentUserMemberId: null,
  memberDependantsList: [],
  isNurseConsultationSelected: false,
  selectedDependent: null,
  queryParamConsultationId: "",
  queryParamDocMedsBundles: false,
  selectedConsultation: null,
  consultationOutcomeResponse: null,
  consultationRatesResponse: null,
  consultationType: MemberConsultationType.SYMPTOM_ASSESSMENT,
  userRating: {
    rating: 0,
    comment: "",
    serviceType: ServiceRating.CONSULTATION,
    ratedServiceId: "",
  },
  consultationAvailabilityData: defaultAvailabilityData,
  startConsultationTime: null,
  isDirectToDoctorSelected: false,
  primarySymptomIds: [],
  memberServiceOfferingUsages: null,
  primarySymptomsResponse: null,
  selectedVoucherOrPromo: null,
  contractsToSign: null,
  oneVoucherPin: undefined,
  oneVoucherRedemptionError: undefined,
  paymentReceiptResponse: null,
  isFromOrderSummary: false,
  isFromMyPurchases: false,
  selectedDiscountVoucher: null,
  capturedDiscountVoucherCode: null,
  applyDiscountVoucherResponse: null,
  applyDiscountVoucherResponseError: null,
  isUseVoucherLater: false,
  matchedConsultation: null,
  skipPayment: false,
  isResumingPaymentFlow: false,
  detectionError: null,
  paymentMethod: undefined,
  consultationPaymentMemberLedgerId: null,
};

export const eventNames = {
  BACK_BUTTON_CLICKED: "BACK_BUTTON_CLICKED",
  GO_HOME_BUTTON_CLICKED: "GO_HOME_BUTTON_CLICKED",
  NEXT_BUTTON_CLICKED: "NEXT_BUTTON_CLICKED",
  CONTINUE_BUTTON_CLICKED: "CONTINUE_BUTTON_CLICKED",
  MORE_INFO_BUTTON_CLICKED: "MORE_INFO_BUTTON_CLICKED",
  START_CONSULTATION_BUTTON_CLICKED: "START_CONSULTATION_BUTTON_CLICKED",
  DEPENDANT_SELECTED: "DEPENDANT_SELECTED",
  ACTIVE_CONSULTATION_SELECTED: "ACTIVE_CONSULTATION_SELECTED",
  USER_REDIRECTED: "USER_REDIRECTED",
  RATING_TRY_AGAIN: "RATING_TRY_AGAIN",
  DIRECT_TO_DOCTOR_BUTTON_CLICKED: "DIRECT_TO_DOCTOR_BUTTON_CLICKED",
  DOCTOR_MEDICINE_CONSULT_BUTTON_CLICKED:
    "DOCTOR_MEDICINE_CONSULT_BUTTON_CLICKED",
  FIND_A_DOCTOR_BUTTON_CLICKED: "FIND_A_DOCTOR_BUTTON_CLICKED",
  NURSE_CONSULTATION_BUTTON_CLICKED: "NURSE_CONSULTATION_BUTTON_CLICKED",
  SKIP_BUTTON_CLICKED: "SKIP_BUTTON_CLICKED",
  SEE_CONDITIONS_BUTTON_CLICKED: "SEE_CONDITIONS_BUTTON_CLICKED",
  PRIMARY_SYMPTOM_BUTTON_CLICKED: "PRIMARY_SYMPTOM_BUTTON_CLICKED",
  CARD_PAYMENT_BUTTON_CLICKED: "CARD_PAYMENT_BUTTON_CLICKED",
  ONE_VOUCHER_BUTTON_CLICKED: "ONE_VOUCHER_BUTTON_CLICKED",
  VOUCHER_BUTTON_CLICKED: "VOUCHER_BUTTON_CLICKED",
  ONE_VOUCHER_CONTINUE_BUTTON_CLICKED: "ONE_VOUCHER_CONTINUE_BUTTON_CLICKED",
  USER_REQUESTED_PAYMENT: "USER_REQUESTED_PAYMENT",
  VIEW_RECEIPT_BUTTON_CLICKED: "VIEW_RECEIPT_BUTTON_CLICKED",
  RATE_SERVICE_BUTTON_CLICKED: "RATE_SERVICE_BUTTON_CLICKED",
  DISCOUNT_VOUCHER_SELECTED: "DISCOUNT_VOUCHER_SELECTED",
  APPLY_DISCOUNT_VOUCHER: "APPLY_DISCOUNT_VOUCHER",
  ADD_A_DISCOUNT_VOUCHER_CLICKED: "ADD_A_DISCOUNT_VOUCHER_CLICKED",
};

export const intercareFlowStateMachine = setup({
  types: {
    context: {} as Context,
  },
  actions: {
    addActiveConsultationListResponseToContext: assign(({ event }) => {
      return { activeConsultationListResponse: event.output };
    }),
    addMembersProfileResponseToContext: assign(({ event }) => {
      return { memberProfileResponse: event.output };
    }),
    addMembersListToContext: assign(({ event }) => {
      const unu01Membership = findUNU01Membership(event.output.memberships);
      return { membersList: unu01Membership?.members || [] };
    }),
    addMembersDependantsListToContext: assign(({ event }) => {
      return { memberDependantsList: event.output };
    }),
    addMemberServiceOfferingUsageResponseToContext: assign(({ event }) => {
      return { memberServiceOfferingUsages: event.output.body };
    }),
    addPrimarySymptomsResponseToContext: assign(({ event }) => {
      return { primarySymptomsResponse: event.output };
    }),
    addSelectedDependentToContext: assign(({ event }) => {
      return { selectedDependent: event.input };
    }),
    resetSelectedDependent: assign(() => {
      return { selectedDependent: null };
    }),
    addConsultationOutcomeResponseToContext: assign(({ event }) => {
      return { consultationOutcomeResponse: event.input || event.output };
    }),
    addMatchedConsultationToContext: assign(({ event }) => {
      return {
        matchedConsultation: event.output || null,
        detectionError: null,
      };
    }),
    addDetectionErrorToContext: assign(({ event }) => {
      return {
        detectionError: event.error as Error,
        matchedConsultation: null,
      };
    }),
    addSelectedConsultationToContext: assign(({ event }) => {
      return { selectedConsultation: event.input };
    }),
    addIsDirectToDoctorToContext: assign(() => {
      return {
        isDirectToDoctorSelected: true,
        isNurseConsultationSelected: false,
        isDoctorMedicineBundleSelected: false,
      };
    }),
    addIsDoctorMedicineBundleToContext: assign(() => {
      return {
        isDirectToDoctorSelected: false,
        isNurseConsultationSelected: false,
        isDoctorMedicineBundleSelected: true,
      };
    }),
    removeIsDoctorMedicineBundleToContext: assign(() => {
      return { isDoctorMedicineBundleSelected: false };
    }),
    addIsNurseConsultationToContext: assign(() => {
      return {
        isNurseConsultationSelected: true,
        isDirectToDoctorSelected: false,
        isDoctorMedicineBundleSelected: false,
      };
    }),
    removeIsDirectToDoctorToContext: assign(() => {
      return { isDirectToDoctorSelected: false };
    }),
    addStartConsultationResponseToContext: assign(({ event }) => {
      return { selectedConsultation: event.input || event.output };
    }),
    addUserRatingToContext: assign(({ event }) => {
      return { userRating: event.input || event.output };
    }),
    addConsultationRatesResponseToContext: assign(({ event }) => {
      return { consultationRatesResponse: event.output };
    }),
    addPrimarySymptomIdsToContext: assign(({ event }) => {
      return { primarySymptomIds: [event.input] };
    }),
    addSelectedVoucherOrPromoToContext: assign(({ event }) => {
      return { selectedVoucherOrPromo: event.input };
    }),
    addContractsToSignToContext: assign(({ event }) => {
      return { contractsToSign: event.output };
    }),
    addOneVoucherPinToContext: assign(({ event }) => {
      return {
        oneVoucherPin: event.input,
      };
    }),
    addInitiatePaymentResponseErrorToContext: assign(({ event }) => {
      return {
        oneVoucherRedemptionError: event.error,
      };
    }),
    clearOneVoucherRedemptionResponseAndPin: assign(() => {
      return {
        oneVoucherRedemptionError: null,
        oneVoucherPin: undefined,
      };
    }),
    clearSelectedDiscountVoucher: assign(() => {
      return {
        selectedDiscountVoucher: null,
      };
    }),
    clearResumingPaymentFlowContext: assign(() => {
      return {
        isResumingPaymentFlow: false,
        matchedConsultation: null,
      };
    }),
    redirectToIntercareUrl: ({ context }) => {
      const redirectUrl =
        context.selectedConsultation?.redirectUrl ||
        context.consultationOutcomeResponse?.redirectUrl;
      if (redirectUrl) {
        window.open(redirectUrl, "_blank");
      }
    },

    setPaymentMethodToCard: assign({ paymentMethod: "card" as const }),

    setPaymentMethodToVoucher: assign({
      paymentMethod: "voucher" as const,
    }),

    resetResumingPaymentFlow: assign({
      isResumingPaymentFlow: false,
    }),

    clearPaymentMethod: assign({ paymentMethod: undefined }),

    clearConsultationPaymentMemberLedgerId: assign({
      consultationPaymentMemberLedgerId: null,
    }),

    setMemberLedgerIdFromEvent: assign(({ event }) => ({
      consultationPaymentMemberLedgerId: (
        event as DoneActorEvent<InitiateConsultationPaymentResponse>
      ).output.memberLedgerId,
    })),
    addPaymentReceiptResponseToContext: assign(({ event }) => {
      return {
        paymentReceiptResponse: event.output,
      };
    }),
    addIsFromOrderSummaryToContext: assign(() => {
      return { isFromOrderSummary: true };
    }),
    addSelectedDiscountVoucherToContext: assign(({ event }) => {
      return {
        selectedDiscountVoucher: event.input || null,
        isUseVoucherLater: false,
      };
    }),
    addCapturedDiscountVoucherCodeToContext: assign(({ event }) => {
      return {
        capturedDiscountVoucherCode: event.input.voucherCode || null,
        isUseVoucherLater: event.input.isUseVoucherLater || false,
      };
    }),
    addApplyDiscountVoucherResponseToContext: assign(({ event }) => {
      return {
        applyDiscountVoucherResponse: event.output || null,
        selectedDiscountVoucher: event.output.memberOfferingUsage || null,
      };
    }),
    addApplyDiscountVoucherResponseErrorToContext: assign(({ event }) => {
      return { applyDiscountVoucherResponseError: event.error || null };
    }),
    removeApplyDiscountVoucherResponseErrorFromContext: assign(() => {
      return {
        applyDiscountVoucherResponseError: null,
        capturedDiscountVoucherCode: null,
        isUseVoucherLater: false,
      };
    }),
    clearCapturedDiscountVoucher: assign(() => {
      return {
        applyDiscountVoucherResponseError: null,
        applyDiscountVoucherResponse: null,
        capturedDiscountVoucherCode: null,
      };
    }),
    clearOnApplyDiscountVoucher: assign(() => {
      return {
        applyDiscountVoucherResponseError: null,
        applyDiscountVoucherResponse: null,
        selectedDiscountVoucher: null,
      };
    }),
  },
  actors: {
    getActiveConsultations: fromPromise(async () => {
      return await getMemberConsultations();
    }),
    /**
     * Find existing consultation by memberId and chargeCode
     * Used for Consultation Idempotency detection
     * Only invoked when VITE_APP_FEATURE_ENABLE_SINGLE_CONSULTATION_ID toggle is enabled
     */
    findExistingConsultation: fromPromise(
      async ({
        input,
      }: {
        input: { context: Context };
      }): Promise<ConsultationMatch | null> => {
        const { context } = input;

        const memberId =
          context.selectedDependent?.memberId || context.currentUserMemberId;

        const chargeCode = getChargeCodeForConsultationType(context);

        if (!memberId || !chargeCode) {
          return null;
        }
        const consultations = await getMemberConsultations(
          GetMemberProductPaymentRatesStatus.ACTIVE
        );

        const consultationsList = Array.isArray(consultations)
          ? consultations
          : [];

        const matchedConsultation = consultationsList.find(
          (c) => c.memberId === memberId && c.chargeCode === chargeCode
        );

        if (!matchedConsultation) {
          return null;
        }

        const consultationMatch: ConsultationMatch = {
          consultationId: matchedConsultation.consultationId,
          memberId: matchedConsultation.memberId,
          chargeCode: matchedConsultation.chargeCode,
          outcome: matchedConsultation.outcome || "",
          redirectUrl: matchedConsultation.redirectUrl || null,
          status: matchedConsultation.status || "",
          startedOn: matchedConsultation.startedOn,
          chargeAmountFormatted:
            matchedConsultation.chargeAmountFormatted || null,
          serviceProviderName: matchedConsultation.serviceProviderName || null,
          mainMemberId: matchedConsultation.mainMemberId || null,
          isDependant: matchedConsultation.isDependant,
          consultationTypeName:
            matchedConsultation.consultationTypeName || undefined,
        };

        return consultationMatch;
      }
    ),
    fetchingPrimarySymptoms: fromPromise(async () => {
      return await getDirectToDoctorPrimarySymptoms(Status.ACTIVE);
    }),
    getMemberships: fromPromise(async () => {
      return await getMemberships();
    }),
    getMemberDependants: fromPromise(async () => {
      return await getMembershipDependents();
    }),
    getMemberProfile: fromPromise(async () => {
      return await getMemberProfile();
    }),
    getMemberServiceOfferingUsage: fromPromise(async () => {
      return await getMemberServiceOfferingUsage(
        MemberServiceOfferingType.CONSULTATIONS
      );
    }),
    getConsultationOutcome: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        return await getMemberConsultationOutcome(
          getConsultationId(input.context)
        );
      }
    ),
    getMemberRequestPaymentOutcome: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        const consultationId = getConsultationId(input.context);
        if (!consultationId) {
          throw new Error("ConsultationId is required for request-payment");
        }
        return await memberRequestPayment(consultationId);
      }
    ),
    getMemberConsultationRates: fromPromise(async () => {
      return await getMemberProductPaymentRates();
    }),
    getContractsToSign: fromPromise(async () => {
      return await getContractsToSign();
    }),
    initiateConsultationPaymentService: fromPromise(
      async ({
        input,
      }: {
        input: InitiateConsultationPaymentParams;
      }): Promise<InitiateConsultationPaymentResponse> => {
        const {
          consultationId,
          patientId,
          productCode,
          offeringId,
          productAmount,
        } = input;

        if (!consultationId) {
          throw new Error(
            "ConsultationId is required for initiate-consultation-payment"
          );
        }
        if (!patientId) {
          throw new Error(
            "PatientId is required for initiate-consultation-payment"
          );
        }
        if (!productCode) {
          throw new Error(
            "ProductCode is required for initiate-consultation-payment"
          );
        }
        if (!offeringId) {
          throw new Error(
            "OfferingId is required for initiate-consultation-payment"
          );
        }
        if (productAmount <= 0) {
          throw new Error(
            "ProductAmount must be greater than 0 for initiate-consultation-payment"
          );
        }

        return await initiateConsultationPayment(input);
      }
    ),
    startConsultation: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        const selectedMemberId =
          input.context.selectedDependent?.memberId ||
          input.context.membersList.find(
            (member) =>
              member.membershipDetails.type === MembershipType.MAIN_MEMBER
          )?.memberDetails?.id ||
          "";

        let consultationType: MemberConsultationType =
          input.context.consultationType;

        if (input.context.isDirectToDoctorSelected) {
          consultationType = MemberConsultationType.DIGITAL_DOCTOR_CONSULT;
        }

        if (input.context.isDoctorMedicineBundleSelected) {
          consultationType =
            MemberConsultationType.DIGITAL_DOCTOR_MEDICINE_BUNDLE_CONSULT;
        }

        const primarySymptomIds = input.context.primarySymptomIds || [];

        const offeringId = getOfferingId(input.context);
        if (!offeringId) {
          throw new Error(
            "Unable to determine offering ID for consultation type"
          );
        }

        return await startMemberConsultation(
          consultationType,
          selectedMemberId,
          offeringId,
          primarySymptomIds.length > 0 ? primarySymptomIds : undefined,
          getMemberServiceOfferingUsageId(input.context),
          false,
          getOneVoucherPin(input.context)
        );
      }
    ),
    updateMemberDependents: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        return updateMembershipDependents({
          dependents: input.context.selectedDependent
            ? [input.context.selectedDependent]
            : [],
        });
      }
    ),
    submitRating: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        return await submitRating({
          rating: input.context.userRating.rating,
          comment: input.context.userRating?.comment || "",
          serviceType:
            input.context.userRating?.serviceType || ServiceRating.CONSULTATION,
          ratedServiceId: getConsultationId(input.context),
        });
      }
    ),
    fetchPaymentReceipt: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        if (!input.context.queryParamConsultationId) {
          return null;
        } else {
          return getConsultationPaymentReceipt(
            input?.context?.queryParamConsultationId
          );
        }
      }
    ),
    applyDiscountVoucher: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        const response = await applyDiscountVoucher(
          input.context.capturedDiscountVoucherCode || "",
          input.context.currentUserMemberId || ""
        );
        if (
          !isDiscountVoucherApplicableToConsultation(
            response.memberOfferingUsage,
            input.context
          )
        ) {
          throw new Error(ApplyVoucherErrors.DISCOUNT_VOUCHER_NOT_APPLICABLE);
        }
        return response;
      }
    ),
  },
  guards: {
    hasConsultationIdInQueryParam: function ({ context }) {
      return !!context.queryParamConsultationId;
    },
    hasDocMedsBundlesInQueryParam: function ({ context }) {
      return context.queryParamDocMedsBundles !== false;
    },
    isOutsideOperatingHours: function ({ context }) {
      const currentTime = getCurrentFormattedTime();
      const currentDay = getCurrentFormattedDay();

      const now = new Date();

      const isChristmas = now.getDate() === 25 && now.getMonth() === 11;
      const isNewYearsDay = now.getDate() === 1 && now.getMonth() === 0;
      if (isChristmas || isNewYearsDay) {
        return true;
      }
      const isNewYearsEve = now.getMonth() === 11 && now.getDate() === 31;
      if (isNewYearsEve) {
        return currentTime < "07:00" || currentTime > "12:45";
      }
      const isChristmasEve = now.getMonth() === 11 && now.getDate() === 24;
      if (isChristmasEve) {
        return currentTime < "07:00" || currentTime > "12:45";
      }

      try {
        if (isPublicHolidayInSouthAfrica()) {
          return currentTime < "09:00" || currentTime > "12:45";
        }
      } catch (error) {
        console.error("Error checking for public holiday:", error);
      }

      const operatingHours = context.consultationAvailabilityData.find(
        (day) => day.day === currentDay
      );
      const [openingTime, closingTime] = operatingHours?.operatingHours.split(
        " - "
      ) as any;

      const isOutsideOperatingHours =
        currentTime < openingTime || currentTime > closingTime;
      return isOutsideOperatingHours;
    },
    hasNoActiveConsultationsAndIsOutsideOperatingHours: function ({ context }) {
      const hasActiveConsultations =
        context.activeConsultationListResponse.length > 0;

      const currentTime = getCurrentFormattedTime();
      const currentDay = getCurrentFormattedDay();

      const operatingHours = context.consultationAvailabilityData.find(
        (day) => day.day === currentDay
      );
      const [openingTime, closingTime] = operatingHours?.operatingHours.split(
        " - "
      ) as any;

      const isOutsideOperatingHours =
        currentTime < openingTime || currentTime > closingTime;
      return isOutsideOperatingHours && !hasActiveConsultations;
    },
    hasAtLeastOneActiveConsultations: function ({ context }) {
      return context.activeConsultationListResponse.length > 0;
    },
    hasDependentsAndNoActiveConsultations: function ({ context }) {
      return (
        context.activeConsultationListResponse.length === 0 &&
        context.memberDependantsList.length > 0
      );
    },
    hasNoDependentsAndNoActiveConsultations: function ({ context }) {
      return (
        context.activeConsultationListResponse.length === 0 &&
        context.memberDependantsList.length === 0
      );
    },
    hasActiveConsultationsAndIsOutsideOperatingHours: function ({ context }) {
      const hasActiveConsultations =
        context.activeConsultationListResponse.length > 0;

      const currentTime = getCurrentFormattedTime();
      const currentDay = getCurrentFormattedDay();

      const operatingHours = context.consultationAvailabilityData.find(
        (day) => day.day === currentDay
      );
      const [openingTime, closingTime] = operatingHours?.operatingHours.split(
        " - "
      ) as any;

      return (
        (currentTime <= openingTime || currentTime >= closingTime) &&
        hasActiveConsultations
      );
    },
    hasDependents: function ({ context }) {
      const result = (context.memberDependantsList?.length || 0) > 0;
      log.guard("hasDependents", {
        result,
        dependentsCount: context.memberDependantsList?.length || 0,
        selectedDependent: context.selectedDependent,
        activeConsultationsCount:
          context.activeConsultationListResponse?.length || 0,
        hasActiveConsultations:
          (context.activeConsultationListResponse?.length || 0) > 0,
      });
      return result;
    },
    shouldEnableDependentBackNavigation: function ({ context }) {
      const isIdempotencyEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_SINGLE_CONSULTATION_ID
      );
      const hasDependents = (context.memberDependantsList?.length || 0) > 0;
      const result = isIdempotencyEnabled && hasDependents;
      log.guard("shouldEnableDependentBackNavigation", {
        result,
        isIdempotencyEnabled,
        hasDependents,
        dependentsCount: context.memberDependantsList?.length || 0,
      });
      return result;
    },
    shouldShowDependentSelection: function ({ context }) {
      const hasDependentsList = (context.memberDependantsList?.length || 0) > 0;
      const noDependentSelected = context.selectedDependent === null;
      const result = hasDependentsList && noDependentSelected;

      log.guard("shouldShowDependentSelection", {
        result,
        hasDependentsList,
        noDependentSelected,
        dependentsCount: context.memberDependantsList?.length || 0,
        selectedDependent: context.selectedDependent,
        activeConsultationsCount:
          context.activeConsultationListResponse?.length || 0,
        logic: `${hasDependentsList} (has dependents) && ${noDependentSelected} (none selected) = ${result}`,
      });

      return result;
    },
    selectedConsultationHasOutstandingPayment: function ({ context }) {
      if (!context.selectedConsultation) {
        return false;
      }

      const { chargeAmountFormatted, chargeCode, outcome } =
        context.selectedConsultation;

      return Boolean(
        isPaymentPendingOutcome(outcome) && chargeAmountFormatted && chargeCode
      );
    },
    selectedConsultationHasOutstandingRefund: function ({ context }) {
      return (
        context.selectedConsultation?.outcome ===
        MemberConsultationOutcome.REFUND_REQUESTED
      );
    },
    selectedConsultationHasOutstandingPaymentWithNoCharge: function ({
      context,
    }) {
      if (!context.selectedConsultation) {
        return false;
      }

      const { chargeAmountFormatted, chargeCode, outcome } =
        context.selectedConsultation;

      return Boolean(
        isPaymentPendingOutcome(outcome) &&
        (!chargeAmountFormatted || !chargeCode)
      );
    },
    userHasFindAHospitalOutcome: function ({ context }) {
      return (
        context.consultationOutcomeResponse?.outcome ===
        MemberConsultationOutcome.EMERGENCY_CONSULT
      );
    },
    userHasArchivedConsultation: function ({ context }) {
      return (
        context.consultationOutcomeResponse?.status ===
        MemberConsultationStatus.ARCHIVED
      );
    },
    userHasUnsuccessfulPaymentOutcome: function ({ context }) {
      return (
        context.consultationOutcomeResponse?.outcome ===
        MemberConsultationOutcome.PAYMENT_FAILED
      );
    },
    userHasFindADoctorOutcome: function ({ context }) {
      return (
        context.consultationOutcomeResponse?.outcome ===
        MemberConsultationOutcome.INPERSON_CONSULT
      );
    },
    activeUserHasCompletedOutcome: function ({ context }) {
      return (
        context.consultationOutcomeResponse?.outcome ===
        MemberConsultationOutcome.DIGITAL_CONSULT_ENDED
      );
    },
    selectedConsultationHasNoRefund: function ({ context }) {
      return (
        context.selectedConsultation?.outcome ===
        MemberConsultationOutcome.NO_REFUND
      );
    },
    hasActiveConsultations: function ({ context }) {
      return context.activeConsultationListResponse.length > 0;
    },
    userHasCompletedOutcome: function ({ context }) {
      return (
        context.consultationOutcomeResponse?.outcome ===
          MemberConsultationOutcome.DIGITAL_CONSULT_ENDED ||
        context.consultationOutcomeResponse?.outcome ===
          MemberConsultationOutcome.NO_REFUND
      );
    },
    userHasOutstandingRefund: function ({ context }) {
      return (
        context.consultationOutcomeResponse?.outcome ===
        MemberConsultationOutcome.REFUND_REQUESTED
      );
    },
    timeoutExceeded: function ({ context }) {
      const currentDate = new Date();
      const consultationStartTime = context.startConsultationTime;

      if (consultationStartTime) {
        return (
          Number(currentDate.getTime() - consultationStartTime.getTime()) /
            1000 >
          MAX_POLLING_TIME_IN_SECONDS
        );
      }

      return false;
    },
    hasNoActiveConsultations: function ({ context }) {
      return context.activeConsultationListResponse.length === 0;
    },
    isDirectToDoctorSelected: function ({ context }) {
      return context.isDirectToDoctorSelected;
    },
    isDoctorMedicineBundleSelected: function ({ context }) {
      return context.isDoctorMedicineBundleSelected;
    },
    isNurseConsultationSelected: function ({ context }) {
      return context.isNurseConsultationSelected;
    },
    hasNoConsultationTypeSelected: function ({ context }) {
      return (
        !context.isDirectToDoctorSelected &&
        !context.isDoctorMedicineBundleSelected &&
        !context.isNurseConsultationSelected
      );
    },
    isSelectedDependentNotMainMember: function ({ context }) {
      return (
        context.selectedDependent?.memberId !== context.currentUserMemberId &&
        isFeatureEnabled(
          FeatureToggleIdentifier.ENABLE_DEPENDANT_SERVICE_OFFERING
        )
      );
    },
    hasPrimarySymptoms: function ({ context }) {
      return context.primarySymptomIds.length > 0;
    },
    shouldSwapScreenOrderForDependents: function ({ context }) {
      const isIdempotencyEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_SINGLE_CONSULTATION_ID
      );

      if (!isIdempotencyEnabled) {
        log.guard("shouldSwapScreenOrderForDependents", {
          result: false,
          reason: "idempotency toggle disabled",
        });
        return false;
      }

      const result = shouldSwapScreenOrder(context);
      log.guard("shouldSwapScreenOrderForDependents", {
        result,
        hasDependents: context.memberDependantsList?.length > 0,
        dependentsCount: context.memberDependantsList?.length,
        hasQueryParams:
          Boolean(context.queryParamConsultationId) ||
          (context.primarySymptomIds?.length || 0) > 0 ||
          Boolean(context.queryParamDocMedsBundles),
        queryParamConsultationId: context.queryParamConsultationId,
        primarySymptomIds: context.primarySymptomIds,
        queryParamDocMedsBundles: context.queryParamDocMedsBundles,
        dependentAlreadySelected: context.selectedDependent !== null,
        selectedDependent: context.selectedDependent,
        isDirectToDoctorSelected: context.isDirectToDoctorSelected,
        isNurseConsultationSelected: context.isNurseConsultationSelected,
      });
      return result;
    },
    shouldEnterMatchingState: function () {
      const isIdempotencyEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_SINGLE_CONSULTATION_ID
      );

      return isIdempotencyEnabled;
    },
    shouldSkipActiveConsultationsScreen: function () {
      return isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_SINGLE_CONSULTATION_ID
      );
    },
    hasMatchedConsultationWithPaymentRequested: function ({ context }) {
      const { matchedConsultation } = context;
      if (!matchedConsultation) {
        return false;
      }
      return (
        getConsultationRoute(matchedConsultation) ===
        ConsultationRoute.PAYMENT_FLOW
      );
    },
    hasMatchedConsultationWithPaymentSucceeded: function ({ context }) {
      const { matchedConsultation } = context;
      if (!matchedConsultation) {
        return false;
      }
      return (
        getConsultationRoute(matchedConsultation) ===
        ConsultationRoute.INTERCARE_LINK
      );
    },
    hasMatchedConsultationWithUnknownOutcome: function ({ context }) {
      const { matchedConsultation } = context;
      if (!matchedConsultation) {
        return false;
      }
      return (
        getConsultationRoute(matchedConsultation) ===
        ConsultationRoute.DEFER_TO_WORKFLOW
      );
    },
    isPromoCodeFeatureEnabled: function () {
      const isPromoCodesFeatureEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_PROMO_CODES
      );

      return isPromoCodesFeatureEnabled;
    },
    isEnhancedPaymentOptionsEnabled: function () {
      const result = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_ENHANCED_PAYMENT_OPTIONS
      );
      log.guard("isEnhancedPaymentOptionsEnabled", { result });
      return result;
    },
    isEnhancedPaymentOptionsEnabledAndPaymentNotSkipped: function ({
      context,
    }) {
      const isEnhancedPaymentOptionsEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_ENHANCED_PAYMENT_OPTIONS
      );
      const result = isEnhancedPaymentOptionsEnabled && !context.skipPayment;
      log.guard("isEnhancedPaymentOptionsEnabledAndPaymentNotSkipped", {
        result,
        isEnhancedPaymentOptionsEnabled,
        skipPayment: context.skipPayment,
      });
      return result;
    },
    isEnhancedPaymentOptionsEnabledAndHasOneVoucherPinInContext: function ({
      context,
    }) {
      const isEnhancedPaymentOptionsEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_ENHANCED_PAYMENT_OPTIONS
      );
      const hasOneVoucherPinInContext = !!context.oneVoucherPin;
      const result = Boolean(
        isEnhancedPaymentOptionsEnabled && hasOneVoucherPinInContext
      );
      log.guard("isEnhancedPaymentOptionsEnabledAndHasOneVoucherPinInContext", {
        result,
        isEnhancedPaymentOptionsEnabled,
        hasOneVoucherPinInContext,
        oneVoucherPin: context.oneVoucherPin,
      });
      return result;
    },
    isAlreadyPaidConsultationError: function ({ event }) {
      const error = (
        event as {
          error?: { status?: number; response?: { status?: number } };
        }
      ).error;
      const status = error?.status ?? error?.response?.status;
      const result = status === 422;
      log.guard("isAlreadyPaidConsultationError", { result, status });
      return result;
    },
    isResumingPaymentFlow: function ({ context }) {
      const result = context.isResumingPaymentFlow === true;
      log.guard("isResumingPaymentFlow", {
        result,
        isResumingPaymentFlow: context.isResumingPaymentFlow,
      });
      return result;
    },

    isCardPaymentSelected: function ({ context }) {
      const result = context.paymentMethod === "card";
      log.guard("isCardPaymentSelected", {
        result,
        paymentMethod: context.paymentMethod,
      });
      return result;
    },
    hasOneVoucherPinInContext: function ({ context }) {
      const result = !!context.oneVoucherPin;
      log.guard("hasOneVoucherPinInContext", {
        result,
        oneVoucherPin: context.oneVoucherPin,
      });
      return result;
    },
    hasCareCardVoucherSelected: function ({ context }) {
      const result =
        !!context.selectedVoucherOrPromo?.id && !context.oneVoucherPin;
      log.guard("hasCareCardVoucherSelected", {
        result,
        selectedVoucherOrPromoId: context.selectedVoucherOrPromo?.id,
        oneVoucherPin: context.oneVoucherPin,
      });
      return result;
    },
    hasMatchedConsultationWithPaymentRequestedAndEnhancedPaymentEnabled:
      function ({ context }) {
        const { matchedConsultation } = context;
        if (!matchedConsultation) {
          log.guard(
            "hasMatchedConsultationWithPaymentRequestedAndEnhancedPaymentEnabled",
            {
              result: false,
              reason: "no matched consultation",
            }
          );
          return false;
        }

        const isPaymentFlow =
          getConsultationRoute(matchedConsultation) ===
          ConsultationRoute.PAYMENT_FLOW;
        const isEnhancedPaymentEnabled = isFeatureEnabled(
          FeatureToggleIdentifier.ENABLE_ENHANCED_PAYMENT_OPTIONS
        );
        const result = isPaymentFlow && isEnhancedPaymentEnabled;
        log.guard(
          "hasMatchedConsultationWithPaymentRequestedAndEnhancedPaymentEnabled",
          {
            result,
            isPaymentFlow,
            isEnhancedPaymentEnabled,
            consultationId: matchedConsultation.consultationId,
          }
        );
        return result;
      },
    hasValidDoctorPromoCodeAndIsEnhancedPaymentsDisabled: function ({
      context,
    }) {
      const isPromoCodesFeatureEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_PROMO_CODES
      );

      const isVoucheringFeatureEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_VOUCHER_REDEMPTION
      );

      const isEnhancedPaymentOptionsEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_ENHANCED_PAYMENT_OPTIONS
      );

      let hasValidPromoCode = false;

      if (context.memberServiceOfferingUsages !== null) {
        const currentDate = new Date();

        if (isVoucheringFeatureEnabled && !isEnhancedPaymentOptionsEnabled) {
          hasValidPromoCode = context.memberServiceOfferingUsages
            .filter(
              (usage) =>
                usage.chargeCode ===
                MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_CONSULT
            )
            .some((promo) => {
              const availableFrom = new Date(promo.availableFrom);
              const availableTo = new Date(promo.availableTo);

              return (
                promo.usageStatus === MemberServiceOfferingStatus.ACTIVE &&
                currentDate >= availableFrom &&
                currentDate <= availableTo
              );
            });
        } else {
          hasValidPromoCode = context.memberServiceOfferingUsages.some(
            (promo) => {
              const availableFrom = new Date(promo.availableFrom);
              const availableTo = new Date(promo.availableTo);

              return (
                promo.usageStatus === MemberServiceOfferingStatus.ACTIVE &&
                currentDate >= availableFrom &&
                currentDate <= availableTo
              );
            }
          );
        }
      }
      return (
        isPromoCodesFeatureEnabled &&
        hasValidPromoCode &&
        !isEnhancedPaymentOptionsEnabled
      );
    },
    isPromoCodeAndVoucherFeatureEnabledAndHasValidNursePromoCode: function ({
      context,
    }) {
      const isPromoCodesFeatureEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_PROMO_CODES
      );

      const isVoucheringFeatureEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_VOUCHER_REDEMPTION
      );

      const isEnhancedPaymentOptionsEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_ENHANCED_PAYMENT_OPTIONS
      );

      let hasValidPromoCode = false;

      if (context.memberServiceOfferingUsages !== null) {
        const currentDate = new Date();

        if (isVoucheringFeatureEnabled && !isEnhancedPaymentOptionsEnabled) {
          hasValidPromoCode = context.memberServiceOfferingUsages
            .filter(
              (usage) =>
                usage.chargeCode ===
                MemberProductPaymentRatesChargeCode.ONLINE_DOCTOR_CONSULTATION
            )
            .some((promo) => {
              const availableFrom = new Date(promo.availableFrom);
              const availableTo = new Date(promo.availableTo);

              return (
                promo.usageStatus === MemberServiceOfferingStatus.ACTIVE &&
                currentDate >= availableFrom &&
                currentDate <= availableTo
              );
            });
        }
      }
      return (
        isPromoCodesFeatureEnabled &&
        hasValidPromoCode &&
        !isEnhancedPaymentOptionsEnabled
      );
    },
    isPromoCodeAndVoucherFeatureEnabledAndHasValidDocMedBundlePromoCode:
      function ({ context }) {
        const isPromoCodesFeatureEnabled = isFeatureEnabled(
          FeatureToggleIdentifier.ENABLE_PROMO_CODES
        );

        let hasValidPromoCode = false;

        if (context.memberServiceOfferingUsages !== null) {
          const currentDate = new Date();

          hasValidPromoCode = context.memberServiceOfferingUsages
            .filter(
              (usage) =>
                usage.chargeCode ===
                MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_MEDICINE_CONSULT
            )
            .some((promo) => {
              const availableFrom = new Date(promo.availableFrom);
              const availableTo = new Date(promo.availableTo);

              return (
                promo.usageStatus === MemberServiceOfferingStatus.ACTIVE &&
                currentDate >= availableFrom &&
                currentDate <= availableTo
              );
            });
        }
        return isPromoCodesFeatureEnabled && hasValidPromoCode;
      },
    hasPaymentReceiptResponse: function ({ context }) {
      return Boolean(context.paymentReceiptResponse);
    },
    isFromMyPurchases: function ({ context }) {
      return (
        Boolean(context.queryParamConsultationId) &&
        Boolean(context.isFromMyPurchases)
      );
    },
    isDiscountVouchersFeatureEnabled: function () {
      return isFeatureEnabled(FeatureToggleIdentifier.ENABLE_DISCOUNT_VOUCHERS);
    },
  },
  schemas: {},
  delays: {
    CONSULTATION_INTERVAL: POLLING_INTERVAL_IN_MILLISECONDS,
  },
}).createMachine({
  context: initialContextValues,
  id: "intercareFlow",
  initial: "start",
  states: {
    start: {
      entry: assign(({ event }) => {
        const entryContext = { ...initialContextValues };
        const {
          primarySymptomIds = [],
          queryParamConsultationId = "",
          queryParamDocMedsBundles = false,
          currentUserMemberId = null,
          isFromMyPurchases = false,
        } = event.input?.context ? event.input.context : {};
        return {
          ...entryContext,
          queryParamConsultationId,
          queryParamDocMedsBundles,
          primarySymptomIds,
          currentUserMemberId,
          isFromMyPurchases,
        };
      }),
      invoke: {
        id: "start",
        input: ({ context }) => ({ context }),
        onDone: [
          {
            target: "fetchingPaymentReceipt",
            guard: {
              type: "isFromMyPurchases",
            },
          },
          {
            target: "fetchingMemberProfile",
            actions: {
              type: "addActiveConsultationListResponseToContext",
            },
          },
        ],
        onError: {
          target: "getActiveConsultationsError",
        },
        src: "getActiveConsultations",
      },
    },
    fetchingPaymentReceipt: {
      invoke: {
        id: "fetchPaymentReceipt",
        input: ({ context }) => ({ context }),
        onDone: [
          {
            target: "showingOrderSummary",
            actions: {
              type: "addPaymentReceiptResponseToContext",
            },
          },
          {
            target: "exitToMyPurchases",
            guard: {
              type: "hasConsultationIdInQueryParam",
            },
          },
          {
            target: "exit",
          },
        ],
        onError: {
          target: "genericError",
        },
        src: "fetchPaymentReceipt",
      },
    },
    showingOrderSummary: {
      always: [
        {
          target: "showOrderSummaryScreen",
          guard: {
            type: "hasPaymentReceiptResponse",
          },
        },
        {
          target: "exitToMyPurchases",
          guard: {
            type: "hasConsultationIdInQueryParam",
          },
        },
        {
          target: "exit",
        },
      ],
    },
    showOrderSummaryScreen: {
      on: {
        [eventNames.VIEW_RECEIPT_BUTTON_CLICKED]: {
          target: "showingPaymentReceipt",
        },
        [eventNames.RATE_SERVICE_BUTTON_CLICKED]: {
          target: "showingServiceRating",
          actions: {
            type: "addIsFromOrderSummaryToContext",
          },
        },
        [eventNames.BACK_BUTTON_CLICKED]: [
          {
            target: "exitToMyPurchases",
            guard: {
              type: "hasConsultationIdInQueryParam",
            },
          },
          {
            target: "exit",
          },
        ],
      },
    },
    showingServiceRating: {
      always: {
        target: "serviceRating",
        guard: { type: "hasPaymentReceiptResponse" },
      },
    },
    showingPaymentReceipt: {
      on: {
        BACK_BUTTON_CLICKED: {
          target: "showingOrderSummary",
        },
      },
    },
    exitToMyPurchases: {
      type: "final",
    },
    fetchingMemberProfile: {
      invoke: {
        id: "fetchingMemberProfile",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "fetchingMemberDetails",
          actions: {
            type: "addMembersProfileResponseToContext",
          },
        },
        onError: {
          target: "genericError",
        },
        src: "getMemberProfile",
      },
    },
    fetchingMemberDetails: {
      invoke: {
        id: "fetchingMemberDetails",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "fetchingMemberDependants",
          actions: {
            type: "addMembersListToContext",
          },
        },
        onError: {
          target: "genericError",
        },
        src: "getMemberships",
      },
    },
    fetchingMemberDependants: {
      invoke: {
        id: "fetchingMemberDependants",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "fetchingMemberServiceOfferingUsage",
          actions: {
            type: "addMembersDependantsListToContext",
          },
        },
        onError: {
          target: "genericError",
        },
        src: "getMemberDependants",
      },
    },
    fetchingMemberServiceOfferingUsage: {
      invoke: {
        id: "fetchingMemberServiceOfferingUsage",
        input: ({ context }) => ({ context }),
        onDone: [
          {
            target: "fetchingPaymentReceipt",
            guard: {
              type: "isFromMyPurchases",
            },
          },
          {
            target: "selectConsultationTypeOrDefault",
            actions: {
              type: "addMemberServiceOfferingUsageResponseToContext",
            },
          },
        ],
        onError: {
          target: "selectConsultationTypeOrDefault",
        },
        src: "getMemberServiceOfferingUsage",
      },
    },
    selectConsultationTypeOrDefault: {
      always: [
        {
          target: "fetchingConsultationOutcome",
          guard: {
            type: "hasConsultationIdInQueryParam",
          },
        },
        {
          target:
            "promptingDirectToDoctorNextStepsWithPromoCodeOrSelectingDependents",
          guard: {
            type: "hasPrimarySymptoms",
          },
          actions: { type: "addIsDirectToDoctorToContext" },
        },
        {
          target: "showingDocMedBundlesInfo",
          guard: {
            type: "hasDocMedsBundlesInQueryParam",
          },
          actions: {
            type: "addIsDoctorMedicineBundleToContext",
          },
        },
        {
          target: "selectingDependents",
          guard: {
            type: "shouldSwapScreenOrderForDependents",
          },
        },
        {
          target: "selectConsultationType",
          guard: {
            type: "hasNoActiveConsultations",
          },
        },
        {
          target: "selectConsultationType",
          guard: {
            type: "shouldSkipActiveConsultationsScreen",
          },
        },
        {
          target: "selectingActiveConsultationOrStartingNewConsultation",
        },
      ],
    },
    selectConsultationType: {
      entry: createStateEntryLogger<Context>(
        "selectConsultationType",
        (context) => ({
          selectedDependent: context.selectedDependent,
          dependentsCount: context.memberDependantsList?.length,
          isDirectToDoctorSelected: context.isDirectToDoctorSelected,
          isNurseConsultationSelected: context.isNurseConsultationSelected,
        })
      ),
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: [
          {
            target: "selectingDependents",
            guard: { type: "shouldEnableDependentBackNavigation" },
          },
          {
            target: "exit",
          },
        ],
        [eventNames.DIRECT_TO_DOCTOR_BUTTON_CLICKED]: [
          {
            target: "promptingPromoCode",
            actions: [{ type: "addIsDirectToDoctorToContext" }],
            guard: {
              type: "hasValidDoctorPromoCodeAndIsEnhancedPaymentsDisabled",
            },
          },
          {
            target: "selectingDependents",
            actions: [{ type: "addIsDirectToDoctorToContext" }],
            guard: {
              type: "shouldSwapScreenOrderForDependents",
            },
          },
          {
            target: "promptingDirectToDoctorNextStepsOrSelectingDependents",
            actions: [{ type: "addIsDirectToDoctorToContext" }],
          },
        ],
        [eventNames.NURSE_CONSULTATION_BUTTON_CLICKED]: [
          {
            target: "promptingPromoCode",
            actions: [{ type: "addIsNurseConsultationToContext" }],
            guard: {
              type: "isPromoCodeAndVoucherFeatureEnabledAndHasValidNursePromoCode",
            },
          },
          {
            target: "selectingDependents",
            actions: [{ type: "addIsNurseConsultationToContext" }],
            guard: {
              type: "shouldSwapScreenOrderForDependents",
            },
          },
          {
            target: "promptingNurseConsultNextStepsOrSelectingDependents",
            actions: [{ type: "addIsNurseConsultationToContext" }],
          },
        ],
        [eventNames.DOCTOR_MEDICINE_CONSULT_BUTTON_CLICKED]: [
          {
            target: "promptingPromoCode",
            actions: [{ type: "addIsDoctorMedicineBundleToContext" }],
            guard: {
              type: "isPromoCodeAndVoucherFeatureEnabledAndHasValidDocMedBundlePromoCode",
            },
          },
          {
            target: "selectingDependents",
            actions: [{ type: "addIsDoctorMedicineBundleToContext" }],
            guard: {
              type: "shouldSwapScreenOrderForDependents",
            },
          },
          {
            target: "showingDocMedBundlesInfo",
            actions: [{ type: "addIsDoctorMedicineBundleToContext" }],
          },
        ],
        [eventNames.SEE_CONDITIONS_BUTTON_CLICKED]: {
          target: "fetchingPrimarySymptoms",
        },
      },
    },
    showingDocMedBundlesInfo: {
      invoke: {
        id: "showingDocMedBundlesInfo",
        input: ({ context }) => ({ context }),
        onDone: {
          actions: {
            type: "addContractsToSignToContext",
          },
        },
        onError: {
          target: "showingDocMedBundlesInfo",
        },
        src: "getContractsToSign",
      },
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: [
          {
            target: "exit",
            guard: {
              type: "hasDocMedsBundlesInQueryParam",
            },
          },
          {
            target: "selectConsultationType",
            actions: [{ type: "removeIsDoctorMedicineBundleToContext" }],
          },
        ],

        [eventNames.CONTINUE_BUTTON_CLICKED]: { target: "showingDeliveryInfo" },
      },
    },
    showingDeliveryInfo: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "showingDocMedBundlesInfo",
        },
        [eventNames.CONTINUE_BUTTON_CLICKED]: [
          {
            target: "refreshingPaymentServiceOfferings",
            guard: { type: "isEnhancedPaymentOptionsEnabled" },
            actions: "addSelectedDependentToContext",
          },
          {
            target: "startingConsultation",
            actions: "addSelectedDependentToContext",
          },
        ],
      },
    },
    fetchingPrimarySymptoms: {
      invoke: {
        id: "showPrimarySymptomsList",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "showPrimarySymptomsList",
          actions: {
            type: "addPrimarySymptomsResponseToContext",
          },
        },
        onError: {
          target: "genericError",
        },
        src: "fetchingPrimarySymptoms",
      },
    },
    showPrimarySymptomsList: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "selectConsultationType",
          actions: [{ type: "removeIsDirectToDoctorToContext" }],
        },
        [eventNames.PRIMARY_SYMPTOM_BUTTON_CLICKED]: [
          {
            target: "promptingPromoCode",
            guard: {
              type: "hasValidDoctorPromoCodeAndIsEnhancedPaymentsDisabled",
            },
            actions: [
              { type: "addPrimarySymptomIdsToContext" },
              { type: "addIsDirectToDoctorToContext" },
            ],
          },
          {
            target: "promptingDirectToDoctorNextStepsOrSelectingDependents",
            actions: [
              { type: "addIsDirectToDoctorToContext" },
              { type: "addPrimarySymptomIdsToContext" },
            ],
          },
        ],
      },
    },
    promptingPromoCode: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "selectConsultationType",
        },
        [eventNames.CONTINUE_BUTTON_CLICKED]: [
          {
            target: "promptingDirectToDoctorNextStepsOrSelectingDependents",
            guard: { type: "isDirectToDoctorSelected" },
            actions: [
              { type: "addIsDirectToDoctorToContext" },
              { type: "addSelectedVoucherOrPromoToContext" },
            ],
          },
          {
            target: "promptingNurseConsultNextStepsOrSelectingDependents",
            guard: { type: "isNurseConsultationSelected" },
            actions: [
              { type: "addIsNurseConsultationToContext" },
              { type: "addSelectedVoucherOrPromoToContext" },
            ],
          },
        ],
        [eventNames.SKIP_BUTTON_CLICKED]: {
          target: "promptingDirectToDoctorNextStepsOrSelectingDependents",
          actions: [{ type: "addIsDirectToDoctorToContext" }],
        },
      },
    },
    promptingNurseConsultNextStepsOrSelectingDependents: {
      entry: createDecisionStateLogger<Context>(
        "promptingNurseConsultNextStepsOrSelectingDependents",
        (context) => ({
          hasDependents: context.memberDependantsList?.length > 0,
          dependentsCount: context.memberDependantsList?.length,
          selectedDependent: context.selectedDependent,
          isNurseConsultationSelected: context.isNurseConsultationSelected,
          activeConsultationsCount:
            context.activeConsultationListResponse?.length,
          hasActiveConsultations:
            context.activeConsultationListResponse?.length > 0,
          decision:
            "Will check shouldShowDependentSelection guard (fixed to prevent re-showing dependent screen)",
        })
      ),
      always: [
        {
          target: "outsideOperatingHours",
          guard: {
            type: "isOutsideOperatingHours",
          },
        },
        {
          target: "selectingDependents",
          guard: {
            type: "shouldShowDependentSelection",
          },
        },
        {
          target: "matchingActiveConsultation",
          guard: {
            type: "shouldEnterMatchingState",
          },
        },
        {
          target: "promptingNurseConsultNextSteps",
        },
      ],
    },
    promptingDirectToDoctorNextStepsWithPromoCodeOrSelectingDependents: {
      entry: createDecisionStateLogger<Context>(
        "promptingDirectToDoctorNextStepsWithPromoCodeOrSelectingDependents",
        (context) => ({
          hasDependents: context.memberDependantsList?.length > 0,
          dependentsCount: context.memberDependantsList?.length,
          selectedDependent: context.selectedDependent,
          isDirectToDoctorSelected: context.isDirectToDoctorSelected,
          activeConsultationsCount:
            context.activeConsultationListResponse?.length,
          hasActiveConsultations:
            context.activeConsultationListResponse?.length > 0,
          decision:
            "Will check shouldShowDependentSelection guard, then shouldEnterMatchingState for idempotency",
        })
      ),
      always: [
        {
          target: "outsideOperatingHours",
          guard: {
            type: "isOutsideOperatingHours",
          },
        },
        {
          target: "selectingDependents",
          guard: {
            type: "shouldShowDependentSelection",
          },
        },
        {
          target: "matchingActiveConsultation",
          guard: {
            type: "shouldEnterMatchingState",
          },
        },
        {
          target: "promptingDirectToDoctorNextStepsWithPromoCode",
          guard: {
            type: "hasValidDoctorPromoCodeAndIsEnhancedPaymentsDisabled",
          },
        },
        {
          target: "promptingDirectToDoctorNextSteps",
        },
      ],
    },
    promptingDirectToDoctorNextStepsOrSelectingDependents: {
      entry: createDecisionStateLogger<Context>(
        "promptingDirectToDoctorNextStepsOrSelectingDependents",
        (context) => ({
          hasDependents: context.memberDependantsList?.length > 0,
          dependentsCount: context.memberDependantsList?.length,
          selectedDependent: context.selectedDependent,
          isDirectToDoctorSelected: context.isDirectToDoctorSelected,
          activeConsultationsCount:
            context.activeConsultationListResponse?.length,
          hasActiveConsultations:
            context.activeConsultationListResponse?.length > 0,
          decision:
            "Will check shouldShowDependentSelection guard (fixed to prevent re-showing dependent screen)",
        })
      ),
      always: [
        {
          target: "outsideOperatingHours",
          guard: {
            type: "isOutsideOperatingHours",
          },
        },
        {
          target: "selectingDependents",
          guard: {
            type: "shouldShowDependentSelection",
          },
        },
        {
          target: "matchingActiveConsultation",
          guard: {
            type: "shouldEnterMatchingState",
          },
        },
        {
          target: "promptingDirectToDoctorNextSteps",
        },
      ],
    },
    refreshingPaymentServiceOfferings: {
      invoke: {
        id: "refreshingPaymentServiceOfferings",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "showingPaymentOptions",
          actions: {
            type: "addMemberServiceOfferingUsageResponseToContext",
          },
        },
        onError: {
          target: "showingPaymentOptions",
        },
        src: "getMemberServiceOfferingUsage",
      },
    },
    showingPaymentOptions: {
      entry: [
        "clearOneVoucherRedemptionResponseAndPin",
        "clearPaymentMethod",
        "clearConsultationPaymentMemberLedgerId",
      ],
      on: {
        [eventNames.CARD_PAYMENT_BUTTON_CLICKED]: [
          {
            target: "waitingForRequestPaymentOutcome",
            guard: { type: "isResumingPaymentFlow" },
            actions: [{ type: "setPaymentMethodToCard" }],
          },
          {
            target: "startingConsultation",
            actions: [{ type: "setPaymentMethodToCard" }],
          },
        ],
        [eventNames.BACK_BUTTON_CLICKED]: [
          {
            target: "selectConsultationType",
            guard: { type: "isResumingPaymentFlow" },
            actions: [
              { type: "clearCapturedDiscountVoucher" },
              { type: "clearResumingPaymentFlowContext" },
            ],
          },
          {
            target: "promptingNurseConsultNextSteps",
            guard: { type: "isNurseConsultationSelected" },
            actions: [{ type: "clearCapturedDiscountVoucher" }],
          },
          {
            target: "showingDeliveryInfo",
            guard: { type: "isDoctorMedicineBundleSelected" },
            actions: [{ type: "clearCapturedDiscountVoucher" }],
          },
          {
            target: "promptingDirectToDoctorNextSteps",
            actions: [{ type: "clearCapturedDiscountVoucher" }],
          },
        ],
        [eventNames.VOUCHER_BUTTON_CLICKED]: {
          target: "selectingVoucher",
          actions: { type: "setPaymentMethodToVoucher" },
        },
        [eventNames.ONE_VOUCHER_BUTTON_CLICKED]: {
          target: "showingOneVoucherRedemption",
          actions: { type: "setPaymentMethodToVoucher" },
        },
        [eventNames.DISCOUNT_VOUCHER_SELECTED]: {
          actions: { type: "addSelectedDiscountVoucherToContext" },
        },
        [eventNames.APPLY_DISCOUNT_VOUCHER]: {
          target: "applyingDiscountVoucher",
          actions: { type: "addCapturedDiscountVoucherCodeToContext" },
          guard: "isDiscountVouchersFeatureEnabled",
        },
        [eventNames.ADD_A_DISCOUNT_VOUCHER_CLICKED]: {
          actions: {
            type: "removeApplyDiscountVoucherResponseErrorFromContext",
          },
        },
      },
    },
    applyingDiscountVoucher: {
      entry: "clearOnApplyDiscountVoucher",
      invoke: {
        id: "applyDiscountVoucher",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "showingPaymentOptions",
          actions: { type: "addApplyDiscountVoucherResponseToContext" },
        },
        onError: {
          target: "showingPaymentOptions",
          actions: { type: "addApplyDiscountVoucherResponseErrorToContext" },
        },
        src: "applyDiscountVoucher",
      },
    },
    selectingVoucher: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "showingPaymentOptions",
        },
        [eventNames.CONTINUE_BUTTON_CLICKED]: [
          {
            target: "waitingForRequestPaymentOutcome",
            guard: { type: "isResumingPaymentFlow" },
            actions: [{ type: "addSelectedVoucherOrPromoToContext" }],
          },
          {
            target: "startingConsultation",
            actions: [{ type: "addSelectedVoucherOrPromoToContext" }],
          },
        ],
      },
    },
    showingOneVoucherRedemption: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "showingPaymentOptions",
        },
        [eventNames.ONE_VOUCHER_CONTINUE_BUTTON_CLICKED]: [
          {
            target: "waitingForRequestPaymentOutcome",
            guard: { type: "isResumingPaymentFlow" },
            actions: [{ type: "addOneVoucherPinToContext" }],
          },
          {
            target: "startingConsultation",
            actions: [{ type: "addOneVoucherPinToContext" }],
          },
        ],
      },
    },
    promptingDirectToDoctorNextStepsWithPromoCode: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "exit",
        },
        [eventNames.CONTINUE_BUTTON_CLICKED]: {
          target: "promptingDirectToDoctorNextSteps",
        },
        [eventNames.SKIP_BUTTON_CLICKED]: {
          target: "promptingDirectToDoctorNextSteps",
          actions: [{ type: "addIsDirectToDoctorToContext" }],
        },
      },
    },
    promptingDirectToDoctorNextSteps: {
      entry: [
        "clearSelectedDiscountVoucher",
        createStateEntryLogger<Context>(
          "promptingDirectToDoctorNextSteps (What Happens Next)",
          (context) => ({
            selectedDependent: context.selectedDependent,
            dependentsCount: context.memberDependantsList?.length,
            isDirectToDoctorSelected: context.isDirectToDoctorSelected,
          })
        ),
      ],
      on: {
        [eventNames.FIND_A_DOCTOR_BUTTON_CLICKED]: {
          target: "findADoctor",
        },
        [eventNames.CONTINUE_BUTTON_CLICKED]: [
          {
            target: "refreshingPaymentServiceOfferings",
            guard: { type: "isEnhancedPaymentOptionsEnabled" },
            actions: { type: "addIsDirectToDoctorToContext" },
          },
          {
            target: "startingConsultation",
            actions: { type: "addIsDirectToDoctorToContext" },
          },
        ],
        [eventNames.BACK_BUTTON_CLICKED]: [
          {
            target: "fetchingPrimarySymptoms",
            guard: { type: "hasPrimarySymptoms" },
          },
          {
            target: "selectConsultationTypeOrDefault",
            actions: { type: "removeIsDirectToDoctorToContext" },
          },
        ],
      },
    },
    selectingActiveConsultationOrStartingNewConsultation: {
      always: [
        {
          target: "outsideOperatingHours",
          guard: {
            type: "hasNoActiveConsultationsAndIsOutsideOperatingHours",
          },
        },
        {
          target: "selectConsultationType",
          guard: {
            type: "shouldSkipActiveConsultationsScreen",
          },
        },
        {
          target: "selectingActiveConsultation",
          guard: {
            type: "hasAtLeastOneActiveConsultations",
          },
        },
        {
          target: "selectingDependents",
          guard: {
            type: "hasDependentsAndNoActiveConsultations",
          },
        },
        {
          target: "promptingNurseConsultNextSteps",
          guard: {
            type: "hasNoDependentsAndNoActiveConsultations",
          },
        },
      ],
    },
    selectingActiveConsultation: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "exit",
        },
        [eventNames.ACTIVE_CONSULTATION_SELECTED]: {
          target: "activeConsultationOutcome",
          actions: [
            { type: "addSelectedConsultationToContext" },
            { type: "addConsultationOutcomeResponseToContext" },
          ],
        },
        [eventNames.START_CONSULTATION_BUTTON_CLICKED]: {
          target: "selectingConsultationTypeOrOutsideOperatingHours",
          actions: [{ type: "resetSelectedDependent" }],
        },
      },
    },
    selectingConsultationTypeOrOutsideOperatingHours: {
      always: [
        {
          target: "outsideOperatingHours",
          guard: {
            type: "hasActiveConsultationsAndIsOutsideOperatingHours",
          },
        },
        {
          target: "selectConsultationType",
        },
      ],
    },
    activeConsultationOutcome: {
      always: [
        {
          target: "outsideOperatingHours",
          guard: {
            type: "hasActiveConsultationsAndIsOutsideOperatingHours",
          },
        },
        {
          target: "outstandingPayment",
          guard: {
            type: "selectedConsultationHasOutstandingPayment",
          },
        },
        {
          target: "outstandingRefund",
          guard: {
            type: "selectedConsultationHasOutstandingRefund",
          },
        },
        {
          target: "promptingNurseConsultNextSteps",
          guard: {
            type: "selectedConsultationHasOutstandingPaymentWithNoCharge",
          },
        },
        {
          target: "findAHospital",
          guard: {
            type: "userHasFindAHospitalOutcome",
          },
        },
        {
          target: "paymentFailure",
          guard: {
            type: "userHasUnsuccessfulPaymentOutcome",
          },
        },
        {
          target: "promptingFindADoctor",
          guard: {
            type: "userHasFindADoctorOutcome",
          },
        },
        {
          target: "serviceRating",
          guard: {
            type: "activeUserHasCompletedOutcome",
          },
        },
        {
          target: "exit",
          guard: {
            type: "selectedConsultationHasNoRefund",
          },
        },
      ],
      on: {
        [eventNames.USER_REDIRECTED]: {
          target: "waitingForConsultationOutcome",
        },
      },
    },
    outstandingPayment: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: [
          {
            target: "selectConsultationType",
            guard: {
              type: "shouldSkipActiveConsultationsScreen",
            },
          },
          {
            target: "selectingActiveConsultation",
          },
        ],
        [eventNames.USER_REQUESTED_PAYMENT]: [
          {
            target: "refreshingPaymentServiceOfferings",
            guard: { type: "isEnhancedPaymentOptionsEnabled" },
            actions: assign({
              isResumingPaymentFlow: true,
            }),
          },
          {
            target: "waitingForRequestPaymentOutcome",
            actions: [{ type: "setPaymentMethodToCard" }],
          },
        ],
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    promptingNurseConsultNextSteps: {
      entry: [
        "clearSelectedDiscountVoucher",
        createStateEntryLogger<Context>(
          "promptingNurseConsultNextSteps (What Happens Next)",
          (context) => ({
            selectedDependent: context.selectedDependent,
            dependentsCount: context.memberDependantsList?.length,
            isNurseConsultationSelected: context.isNurseConsultationSelected,
          })
        ),
      ],
      always: {
        target: "outsideOperatingHours",
        guard: {
          type: "hasActiveConsultationsAndIsOutsideOperatingHours",
        },
      },
      invoke: {
        id: "promptingNurseConsultNextSteps",
        input: ({ context }) => ({ context }),
        onDone: {
          actions: {
            type: "addConsultationRatesResponseToContext",
          },
        },
        onError: {
          target: "genericError",
        },
        src: "getMemberConsultationRates",
      },
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "redirectFromPromptingNextSteps",
        },
        [eventNames.CONTINUE_BUTTON_CLICKED]: [
          {
            target: "refreshingPaymentServiceOfferings",
            guard: { type: "isEnhancedPaymentOptionsEnabled" },
          },
          {
            target: "startingConsultation",
          },
        ],
        [eventNames.MORE_INFO_BUTTON_CLICKED]: {
          target: "promptingNurseConsultMoreInfo",
        },
        [eventNames.FIND_A_DOCTOR_BUTTON_CLICKED]: {
          target: "findADoctor",
        },
      },
    },
    promptingNurseConsultMoreInfo: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "promptingNurseConsultNextSteps",
        },
      },
    },
    redirectFromPromptingNextSteps: {
      always: [
        {
          target: "selectConsultationType",
          guard: {
            type: "hasNoDependentsAndNoActiveConsultations",
          },
        },
        {
          target: "selectConsultationType",
          guard: {
            type: "shouldSkipActiveConsultationsScreen",
          },
        },
        {
          target: "selectingActiveConsultation",
          guard: {
            type: "hasActiveConsultations",
          },
        },
        {
          target: "selectingDependents",
          guard: {
            type: "hasDependentsAndNoActiveConsultations",
          },
        },
        {
          target: "selectConsultationType",
        },
      ],
    },
    selectingDependents: {
      entry: createStateEntryLogger<Context>(
        "selectingDependents",
        (context) => ({
          isDirectToDoctorSelected: context.isDirectToDoctorSelected,
          isNurseConsultationSelected: context.isNurseConsultationSelected,
          isDoctorMedicineBundleSelected:
            context.isDoctorMedicineBundleSelected,
          selectedDependent: context.selectedDependent,
          dependentsCount: context.memberDependantsList?.length,
        })
      ),
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "exit",
        },
        [eventNames.DEPENDANT_SELECTED]: {
          target: "updatingDependentsOrPromptingNextSteps",
          actions: "addSelectedDependentToContext",
        },
      },
    },
    updatingDependentsOrPromptingNextSteps: {
      always: [
        {
          target: "updatingMemberDependents",
          guard: {
            type: "isSelectedDependentNotMainMember",
          },
        },
        {
          target: "promptingDirectToDoctorOrNurseConsultNextSteps",
        },
      ],
    },
    updatingMemberDependents: {
      invoke: {
        id: "updatingMemberDependents",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "promptingDirectToDoctorOrNurseConsultNextSteps",
        },
        onError: {
          target: "genericError",
        },
        src: "updateMemberDependents",
      },
    },
    promptingDirectToDoctorOrNurseConsultNextSteps: {
      always: [
        {
          target: "matchingActiveConsultation",
          guard: {
            type: "shouldEnterMatchingState",
          },
        },
        {
          target: "promptingDirectToDoctorNextSteps",
          guard: {
            type: "isDirectToDoctorSelected",
          },
        },
        {
          target: "promptingNurseConsultNextSteps",
        },
      ],
    },
    matchingActiveConsultation: {
      invoke: {
        id: "matchingActiveConsultation",
        input: ({ context }) => ({ context }),
        src: "findExistingConsultation",
        onDone: {
          actions: assign({
            matchedConsultation: ({ event }) => event.output,
          }),
          target: "routingBasedOnMatch",
        },
        onError: {
          actions: assign({
            detectionError: ({ event }) =>
              event.error instanceof Error
                ? event.error
                : new Error(String(event.error)),
          }),
          target: "genericError",
        },
      },
    },
    routingBasedOnMatch: {
      always: [
        {
          target: "outstandingPayment",
          guard: {
            type: "hasMatchedConsultationWithPaymentRequested",
          },
          actions: assign({
            selectedConsultation: ({ context }) =>
              context.matchedConsultation
                ? mapMatchedToSelectedConsultation(context.matchedConsultation)
                : null,
          }),
        },
        {
          target: "breakoutDisclaimer",
          guard: {
            type: "hasMatchedConsultationWithPaymentSucceeded",
          },
          actions: assign({
            skipPayment: true,
            selectedConsultation: ({ context }) =>
              context.matchedConsultation
                ? mapMatchedToSelectedConsultation(context.matchedConsultation)
                : null,
          }),
        },
        {
          target: "waitingForConsultationOutcome",
          guard: {
            type: "hasMatchedConsultationWithUnknownOutcome",
          },
          actions: assign({
            selectedConsultation: ({ context }) =>
              context.matchedConsultation
                ? mapMatchedToSelectedConsultation(context.matchedConsultation)
                : null,
          }),
        },
        {
          target: "promptingDirectToDoctorNextSteps",
          guard: {
            type: "isDirectToDoctorSelected",
          },
        },
        {
          target: "selectConsultationType",
          guard: {
            type: "hasNoConsultationTypeSelected",
          },
        },
        {
          target: "promptingNurseConsultNextSteps",
        },
      ],
    },
    getActiveConsultationsError: {
      on: {
        [eventNames.CONTINUE_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    startingConsultation: {
      invoke: {
        id: "startingConsultation",
        input: ({ context }) => ({ context }),
        src: "startConsultation",
        onDone: [
          {
            target: "initiatingConsultationPayment",
            guard: { type: "isCardPaymentSelected" },
            actions: {
              type: "addStartConsultationResponseToContext",
            },
          },
          {
            target: "initiatingConsultationPayment",
            guard: { type: "hasOneVoucherPinInContext" },
            actions: {
              type: "addStartConsultationResponseToContext",
            },
          },
          {
            target: "initiatingConsultationPayment",
            guard: { type: "hasCareCardVoucherSelected" },
            actions: {
              type: "addStartConsultationResponseToContext",
            },
          },
          {
            // Fallback: Skip payment initiation for flows where payment is not required
            // (e.g., promo codes that cover full amount, or other edge cases)
            target: "breakoutDisclaimer",
            actions: {
              type: "addStartConsultationResponseToContext",
            },
          },
        ],
        onError: [
          {
            target: "showingOneVoucherRedemption",
            guard: {
              type: "isEnhancedPaymentOptionsEnabledAndHasOneVoucherPinInContext",
            },
            actions: {
              type: "addInitiatePaymentResponseErrorToContext",
            },
          },
          {
            target: "genericError",
          },
        ],
      },
      on: {
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    initiatingConsultationPayment: {
      invoke: {
        id: "initiateConsultationPayment",
        src: "initiateConsultationPaymentService",
        input: ({ context }) => ({
          consultationId: getConsultationId(context),
          patientId: getPatientId(context),
          productAmount: getProductAmount(context),
          productCode: getChargeCodeForConsultationType(context) || "",
          serviceType: getServiceType(context),
          offeringId: getOfferingId(context),
          memberServiceOfferingUsageId:
            getMemberServiceOfferingUsageId(context),
          oneVoucherPin: getOneVoucherPin(context),
          // memberId is always the main member (payer), regardless of who the patient is
          memberId: getMainMemberId(context),
        }),
        onDone: {
          target: "breakoutDisclaimer",
          actions: [
            { type: "resetResumingPaymentFlow" },
            { type: "setMemberLedgerIdFromEvent" },
          ],
        },
        onError: [
          {
            target: "waitingForConsultationOutcome",
            guard: { type: "isAlreadyPaidConsultationError" },
          },
          {
            target: "handlingPaymentFailure",
          },
        ],
      },
      on: {
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    handlingPaymentFailure: {
      entry: "clearConsultationPaymentMemberLedgerId",
      on: {
        [eventNames.CONTINUE_BUTTON_CLICKED]: {
          target: "refreshingPaymentServiceOfferings",
          actions: assign({ isResumingPaymentFlow: true }),
        },
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "exit",
        },
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    breakoutDisclaimer: {
      on: {
        [eventNames.USER_REDIRECTED]: {
          target: "waitingForConsultationOutcome",
          actions: { type: "clearConsultationPaymentMemberLedgerId" },
        },
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "redirectFromBreakoutDisclaimer",
        },
      },
    },
    redirectFromBreakoutDisclaimer: {
      entry: [
        "clearSelectedDiscountVoucher",
        "clearConsultationPaymentMemberLedgerId",
        "clearPaymentMethod",
      ],
      always: [
        {
          target: "refreshingPaymentServiceOfferings",
          guard: {
            type: "isEnhancedPaymentOptionsEnabledAndPaymentNotSkipped",
          },
        },
        {
          target: "selectConsultationType",
          guard: {
            type: "shouldSkipActiveConsultationsScreen",
          },
        },
        {
          target: "promptingDirectToDoctorNextSteps",
          guard: {
            type: "isDirectToDoctorSelected",
          },
        },
        {
          target: "promptingNurseConsultNextSteps",
          guard: {
            type: "isNurseConsultationSelected",
          },
        },
        {
          target: "selectingActiveConsultation",
          guard: {
            type: "hasActiveConsultations",
          },
        },
      ],
    },
    waitingForRequestPaymentOutcome: {
      invoke: {
        id: "waitingForRequestPaymentOutcome",
        input: ({ context }) => ({ context }),
        onDone: {
          // v5: Use initiatingConsultationPayment for PAYMENT_FLOW (unified with NEW_CONSULTATION)
          target: "initiatingConsultationPayment",
        },
        onError: {
          target: "handlingPaymentFailure",
        },
        src: "getMemberRequestPaymentOutcome",
      },
    },
    waitingForConsultationOutcome: {
      after: {
        CONSULTATION_INTERVAL: {
          target: "fetchingConsultationOutcome",
        },
      },
      on: {
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    fetchingConsultationOutcome: {
      invoke: {
        id: "fetchingConsultationOutcome",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "processingConsultationOutcome",
          actions: {
            type: "addConsultationOutcomeResponseToContext",
          },
        },
        onError: {
          target: "genericError",
        },
        src: "getConsultationOutcome",
      },
      on: {
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    processingConsultationOutcome: {
      always: [
        {
          target: "promptingNurseConsultNextSteps",
          guard: {
            type: "userHasArchivedConsultation",
          },
        },
        {
          target: "findAHospital",
          guard: {
            type: "userHasFindAHospitalOutcome",
          },
        },
        {
          target: "paymentFailure",
          guard: {
            type: "userHasUnsuccessfulPaymentOutcome",
          },
        },
        {
          target: "promptingFindADoctor",
          guard: {
            type: "userHasFindADoctorOutcome",
          },
        },
        {
          target: "serviceRating",
          guard: {
            type: "userHasCompletedOutcome",
          },
        },
        {
          target: "outstandingRefund",
          guard: {
            type: "userHasOutstandingRefund",
          },
        },
        {
          target: "exit",
          guard: {
            type: "timeoutExceeded",
          },
        },
        {
          target: "breakoutDisclaimer",
          guard: {
            type: "hasConsultationIdInQueryParam",
          },
        },
        {
          target: "waitingForConsultationOutcome",
        },
      ],
      on: {
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    genericError: {
      entry: "clearConsultationPaymentMemberLedgerId",
      on: {
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    outsideOperatingHours: {
      on: {
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    submittingUserRating: {
      invoke: {
        id: "submittingUserRating",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "submitRatingComplete",
        },
        onError: {
          target: "submitRatingError",
        },
        src: "submitRating",
      },
    },
    submitRatingComplete: {
      on: {
        [eventNames.CONTINUE_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    submitRatingError: {
      on: {
        [eventNames.RATING_TRY_AGAIN]: {
          target: "serviceRating",
        },
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    outstandingRefund: {
      on: {
        [eventNames.CONTINUE_BUTTON_CLICKED]: {
          target: "exit",
        },
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    serviceRating: {
      on: {
        [eventNames.CONTINUE_BUTTON_CLICKED]: {
          target: "submittingUserRating",
          actions: "addUserRatingToContext",
        },
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    findAHospital: {
      type: "final",
    },
    promptingFindADoctor: {
      on: {
        [eventNames.CONTINUE_BUTTON_CLICKED]: {
          target: "findADoctor",
        },
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    findADoctor: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: [
          {
            target: "promptingDirectToDoctorNextSteps",
            guard: {
              type: "isDirectToDoctorSelected",
            },
          },
          { target: "promptingFindADoctor" },
        ],
      },
    },
    paymentFailure: {
      on: {
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    exit: {
      type: "final",
      entry: ["clearPaymentMethod", "clearConsultationPaymentMemberLedgerId"],
    },
  },
});
